import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    AlertTriangle, ArrowLeft, Check, Info, Loader2, Plus, Sparkles, Trash2, X,
} from 'lucide-react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Combobox } from '../../../components/ui/combobox';
import { AiParsedLine, AiStatus, Product } from '../../../types/stock.types';
import { stockAiApi } from '../../../utils/stock-api';
import { cn } from '../../../components/ui/utils';
import SheetFilePicker from './SheetFilePicker';

/** A line the user has accepted, ready to write to the order. */
export interface GeneratedLine {
    productId: number;
    quantity: number;
    sku: string;
    name: string;
    uom: string;
    originalText: string;
    confidence: number | null;
}

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Active products, for resolving unmatched lines by hand. */
    products: Product[];
    /** Fires only when the user confirms. Nothing is saved before this. The files the
     *  sheet was read from ride along so the caller can keep them as evidence (§12.4)
     *  — empty for a pasted-text parse. `aiUsageId` is the ledger row for the call, so
     *  the order can be attributed to what it cost. */
    onConfirm: (
        lines: GeneratedLine[],
        rawInputText: string,
        files: File[],
        aiUsageId: number | null,
    ) => void;
    title?: string;
    /** Pre-fill the list when the caller already collected the text (paste-first form). */
    initialText?: string;
    /** Which input tab to open on — 'upload' for the gear-sheet photo/PDF journey. */
    initialInput?: 'text' | 'upload';
    /**
     * Sheets the caller already collected (photos/PDF, compressed and ready to send).
     * When provided, parsing starts as soon as the dialog opens — the caller did the
     * uploading, so this popup goes straight to being the review step.
     */
    initialFiles?: File[];
}

/** Below this, a match is flagged for a closer look rather than trusted. */
const LOW_CONFIDENCE = 0.75;

/**
 * Generate Order — the shared AI parse + review flow (STOCK.md §2.5).
 *
 * Two steps by design. The user pastes a list — or uploads photo(s)/a PDF of a
 * filled-in paper sheet — and then **reviews what the parser made of it before
 * anything is saved** — §2.5 is explicit that AI output is never committed silently.
 * The original text sits beside every matched line so the reviewer can check the
 * match rather than trust it.
 *
 * Unmatched lines are not failures to hide; they are the honest output of a parser told
 * that guessing is worse than asking. Each one can be resolved to a product by hand or
 * dropped, and the count of unresolved ones is shown on the confirm button so nothing
 * slips through silently. Items the parser never saw can be added by hand in the same
 * review step — the parse doesn't have to be the whole order.
 *
 * §2.6: if the service is unavailable the CTA never appears — the caller shows the
 * enter-it-manually microcopy instead, driven by the same status probe used here.
 *
 * Callers that collected the sheet(s) themselves (the order forms) pass them as
 * `initialFiles`; the parse then starts on open and this dialog is the review step
 * only — uploading in two places would just be two places to get it wrong.
 */
export const GenerateOrderDialog: React.FC<Props> = ({
    open, onOpenChange, products, onConfirm, title = 'Generate Order', initialText,
    initialInput = 'text', initialFiles,
}) => {
    const [status, setStatus] = useState<AiStatus | null>(null);
    const [step, setStep] = useState<'input' | 'review'>('input');
    const [inputMode, setInputMode] = useState<'text' | 'upload'>('text');
    const [text, setText] = useState('');
    /** Photos/PDF pages of the sheet, ready to send — compressed images, PDFs as-is. */
    const [files, setFiles] = useState<File[]>([]);
    const [parsing, setParsing] = useState(false);
    const [error, setError] = useState<{ message: string; retryable: boolean } | null>(null);

    const [lines, setLines] = useState<GeneratedLine[]>([]);
    const [unresolved, setUnresolved] = useState<
        { originalText: string; quantity: number; reason: string; productId: string }[]
    >([]);
    const [rawInputText, setRawInputText] = useState('');
    const [usageId, setUsageId] = useState<number | null>(null);
    const [usedModel, setUsedModel] = useState<string | null>(null);
    /** Item being added by hand in the review step — the parse doesn't have to be
     *  the whole order. */
    const [draft, setDraft] = useState<{ productId: string; quantity: number }>({
        productId: '', quantity: 1,
    });

    /**
     * One confirmation per parse.
     *
     * The dialog stays mounted through its close animation, so a second click — the one
     * that lands when the user thinks nothing happened — fires this again. Accepting the
     * same lines twice is not a harmless repeat: the caller merges by product and SUMS
     * the quantities, so the sheet silently doubles (bugherd: "duplicated the
     * quantities"). A ref, not state, because both clicks are inside the same commit.
     */
    const confirmedRef = useRef(false);

    const productOptions = Array.isArray(products) ? products : [];
    const productSelectOptions = productOptions.map((p) => ({ label: `${p.name} (${p.sku})`, value: String(p.id) }));

    const reset = useCallback(() => {
        setStep('input');
        setText('');
        setFiles([]);
        setError(null);
        setLines([]);
        setUnresolved([]);
        setRawInputText('');
        setUsageId(null);
        setUsedModel(null);
        setDraft({ productId: '', quantity: 1 });
        confirmedRef.current = false;
    }, []);

    /**
     * Runs the parse against explicit input rather than component state, so both the
     * footer button and the open-with-sheets auto-start share one path. A failure
     * leaves the user on the input step — input intact, retry one click away.
     */
    const runParse = useCallback(async (mode: 'text' | 'upload', textValue: string, fileList: File[]) => {
        try {
            setParsing(true);
            setError(null);
            const result = mode === 'upload'
                ? await stockAiApi.parseUpload(fileList)
                : await stockAiApi.parse(textValue);

            setLines(result.matched.map((m: AiParsedLine) => ({
                productId: m.productId,
                quantity: m.quantity,
                sku: m.sku,
                name: m.name,
                uom: m.uom,
                originalText: m.originalText,
                confidence: m.confidence,
            })));
            setUnresolved(result.unmatched.map((u) => ({ ...u, productId: '' })));
            setRawInputText(result.rawInputText);
            setUsageId(result.usageId ?? null);
            setUsedModel(result.model);
            setStep('review');
        } catch (err: any) {
            setError({
                message: err?.data?.message || err?.message || 'Parsing failed.',
                retryable: Boolean(err?.data?.retryable),
            });
        } finally {
            setParsing(false);
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        reset();
        setInputMode(initialInput);
        // The caller's paste-first form hands its text over instead of making the user
        // type it twice — still editable here before generating.
        if (initialText) setText(initialText);
        stockAiApi.status().then(setStatus).catch(() =>
            setStatus({ available: false, model: '', message: 'AI parsing is unavailable.' }));
        // Sheets attached in the calling form go straight to reading — this popup is
        // then just the review step, not a second place to upload.
        if (initialFiles && initialFiles.length > 0) {
            setInputMode('upload');
            setFiles(initialFiles);
            runParse('upload', '', initialFiles);
        }
    }, [open, reset, initialText, initialInput, initialFiles, runParse]);

    const handleParse = () => {
        if (inputMode === 'upload' ? files.length === 0 : !text.trim()) return;
        runParse(inputMode, text, files);
    };

    /** Promote a hand-resolved unmatched line into the accepted list. */
    const resolveUnmatched = (index: number) => {
        const row = unresolved[index];
        const product = productOptions.find((p) => String(p.id) === row.productId);
        if (!product) return;

        setLines((prev) => [...prev, {
            productId: product.id,
            quantity: row.quantity > 0 ? row.quantity : 1,
            sku: product.sku,
            name: product.name,
            uom: product.uom,
            originalText: row.originalText,
            // Hand-resolved, so there is no model confidence to report.
            confidence: null,
        }]);
        setUnresolved((prev) => prev.filter((_, i) => i !== index));
    };

    /** Add an item the parser never saw, straight into the accepted list. */
    const addDraftLine = () => {
        const product = productOptions.find((p) => String(p.id) === draft.productId);
        if (!product) return;
        const quantity = draft.quantity > 0 ? draft.quantity : 1;
        setLines((prev) => {
            // Already in the list — top up its quantity rather than adding a duplicate
            // row the user would have to merge themselves.
            const existing = prev.findIndex((l) => l.productId === product.id);
            if (existing >= 0) {
                return prev.map((l, i) =>
                    i === existing ? { ...l, quantity: l.quantity + quantity } : l);
            }
            return [...prev, {
                productId: product.id,
                quantity,
                sku: product.sku,
                name: product.name,
                uom: product.uom,
                // Nothing was parsed — no original text or model confidence to show.
                originalText: '',
                confidence: null,
            }];
        });
        setDraft({ productId: '', quantity: 1 });
    };

    const unavailable = status !== null && !status.available;
    /**
     * The lines that will actually be written.
     *
     * Whole numbers above zero, which is the same rule the API enforces — and the reason
     * the count on the button is computed from this rather than from `lines`: what it
     * offers to add has to be what gets added.
     */
    const acceptedLines = lines.filter((l) => Number.isInteger(l.quantity) && l.quantity > 0);
    const lowConfidenceCount = lines.filter(
        (l) => l.confidence !== null && l.confidence < LOW_CONFIDENCE).length;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles size={18} className="text-blue-600" />
                        {title}
                    </DialogTitle>
                    <DialogDescription>
                        {step === 'input'
                            ? 'Paste or type the list as it was written. Slang and shorthand are fine.'
                            : 'Check what was matched before adding it. Nothing has been saved yet.'}
                    </DialogDescription>
                </DialogHeader>

                {/* §2.6 — service down */}
                {unavailable && (
                    <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                        <p className="font-medium">AI parsing is unavailable right now.</p>
                        <p className="mt-1">
                            Close this and enter the list manually — the product picker works as normal.
                        </p>
                    </div>
                )}

                {/* Step 1 — input: paste the list, or upload photo(s)/a PDF of the sheet */}
                {step === 'input' && !unavailable && (
                    <>
                        <Tabs value={inputMode}
                              onValueChange={(v) => setInputMode(v as 'text' | 'upload')}>
                            <TabsList>
                                <TabsTrigger value="text">Paste text</TabsTrigger>
                                <TabsTrigger value="upload">Upload sheet</TabsTrigger>
                            </TabsList>

                            <TabsContent value="text">
                                <div>
                                    <Label htmlFor="ai-text">The list</Label>
                                    <Textarea
                                        id="ai-text"
                                        rows={9}
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        placeholder={'40 standards 3.0m\n20 8ft ledgers\na dozen 2.4m boards\n15 swivels'}
                                        className="font-mono text-sm mt-1"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        One item per line works best, but a run-on list is fine too.
                                    </p>
                                </div>
                            </TabsContent>

                            <TabsContent value="upload">
                                <SheetFilePicker files={files} onChange={setFiles} />
                            </TabsContent>
                        </Tabs>

                        {error && (
                            <div className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-4 py-3">
                                <p>{error.message}</p>
                                {error.retryable && (
                                    <p className="text-xs mt-1">
                                        This is usually temporary — try again in a moment.
                                    </p>
                                )}
                            </div>
                        )}

                        <DialogFooter>
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                            <Button onClick={handleParse}
                                    disabled={parsing ||
                                        (inputMode === 'upload' ? files.length === 0 : !text.trim())}>
                                {parsing
                                    ? <Loader2 size={16} className="animate-spin mr-2" />
                                    : <Sparkles size={16} className="mr-2" />}
                                {parsing
                                    ? (inputMode === 'upload' ? 'Reading the sheet…' : 'Reading the list…')
                                    : 'Generate Order'}
                            </Button>
                        </DialogFooter>
                    </>
                )}

                {/* Step 2 — review */}
                {step === 'review' && (
                    <>
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                            <span className="text-emerald-700 font-medium">{lines.length} matched</span>
                            {unresolved.length > 0 && (
                                <span className="text-amber-700 font-medium">
                                    {unresolved.length} need{unresolved.length === 1 ? 's' : ''} your input
                                </span>
                            )}
                            {lowConfidenceCount > 0 && (
                                <span className="text-amber-700">
                                    {lowConfidenceCount} low confidence
                                </span>
                            )}
                        </div>

                        {/* Matched */}
                        {lines.length > 0 && (
                            <div className="divide-y divide-border border border-border rounded-lg">
                                {lines.map((line, index) => {
                                    const low = line.confidence !== null && line.confidence < LOW_CONFIDENCE;
                                    return (
                                        <div key={`${line.productId}-${index}`}
                                             className={cn('p-3', low && 'bg-amber-50/50')}>
                                            <div className="flex items-center gap-3">
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-medium truncate">{line.name}</p>
                                                    <p className="text-xs text-muted-foreground font-mono">
                                                        {line.sku}
                                                    </p>
                                                </div>
                                                <Input
                                                    type="number" inputMode="numeric" min="0"
                                                    value={line.quantity}
                                                    onChange={(e) => setLines((prev) => prev.map((l, i) =>
                                                        i === index ? { ...l, quantity: Number(e.target.value) } : l))}
                                                    className="w-24"
                                                />
                                                <span className="text-xs text-muted-foreground w-12">
                                                    {line.uom}
                                                </span>
                                                <Button variant="ghost" size="icon" aria-label="Remove line"
                                                        className="text-destructive"
                                                        onClick={() => setLines((prev) =>
                                                            prev.filter((_, i) => i !== index))}>
                                                    <Trash2 size={16} />
                                                </Button>
                                            </div>
                                            {/* The original text is the whole point of the review step.
                                                Hand-added lines have none to show. */}
                                            <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-2 flex-wrap">
                                                {line.originalText && (
                                                    <span className="font-mono">"{line.originalText}"</span>
                                                )}
                                                {line.confidence !== null && (
                                                    <span className={cn(low && 'text-amber-700 font-medium')}>
                                                        {low && <AlertTriangle size={11} className="inline mr-0.5" />}
                                                        {Math.round(line.confidence * 100)}% confident
                                                    </span>
                                                )}
                                                {line.confidence === null && (
                                                    <span className="text-blue-700">you picked this</span>
                                                )}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Missed something? Add it by hand — same accepted list. */}
                        <div>
                            <p className="text-sm font-medium mb-2">Add another item</p>
                            <div className="grid grid-cols-[2fr_1fr_auto] gap-2">
                                <Combobox
                                    options={productSelectOptions}
                                    value={draft.productId || undefined}
                                    onChange={(v) => setDraft((prev) => ({ ...prev, productId: v || '' }))}
                                    placeholder="Pick a product"
                                    searchPlaceholder="Search products..."
                                    emptyMessage="No products found."
                                />
                                <Input
                                    type="number" inputMode="numeric" min="1"
                                    value={draft.quantity || ''}
                                    placeholder="Qty"
                                    onChange={(e) => setDraft((prev) => ({
                                        ...prev, quantity: Number(e.target.value),
                                    }))}
                                />
                                <Button size="icon" aria-label="Add item"
                                        disabled={!draft.productId}
                                        onClick={addDraftLine}>
                                    <Plus size={16} />
                                </Button>
                            </div>
                        </div>

                        {/* Unmatched — resolve or drop */}
                        {unresolved.length > 0 && (
                            <div>
                                <p className="text-sm font-medium flex items-center gap-1.5 mb-2">
                                    <AlertTriangle size={14} className="text-amber-600" />
                                    Couldn't match these
                                </p>
                                <div className="space-y-2">
                                    {unresolved.map((row, index) => (
                                        <div key={index}
                                             className="border border-amber-200 bg-amber-50/40 rounded-lg p-3">
                                            <p className="text-sm font-mono">"{row.originalText}"</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">{row.reason}</p>
                                            <div className="grid grid-cols-[2fr_1fr_auto_auto] gap-2 mt-2">
                                                <Combobox
                                                    options={productSelectOptions}
                                                    value={row.productId || undefined}
                                                    onChange={(v) => setUnresolved((prev) => prev.map((r, i) =>
                                                        i === index ? { ...r, productId: v || '' } : r))}
                                                    placeholder="Pick a product"
                                                    searchPlaceholder="Search products..."
                                                    emptyMessage="No products found."
                                                />
                                                <Input
                                                    type="number" inputMode="numeric" min="1"
                                                    value={row.quantity || ''}
                                                    placeholder="Qty"
                                                    onChange={(e) => setUnresolved((prev) => prev.map((r, i) =>
                                                        i === index ? { ...r, quantity: Number(e.target.value) } : r))}
                                                />
                                                <Button size="icon" aria-label="Use this product"
                                                        disabled={!row.productId}
                                                        onClick={() => resolveUnmatched(index)}>
                                                    <Check size={16} />
                                                </Button>
                                                <Button variant="ghost" size="icon" aria-label="Discard line"
                                                        onClick={() => setUnresolved((prev) =>
                                                            prev.filter((_, i) => i !== index))}>
                                                    <X size={16} />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {lines.length === 0 && unresolved.length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-6">
                                Nothing came back from that. Try rewording it, or enter the list manually.
                            </p>
                        )}

                        {usedModel && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                                <Info size={11} />
                                Parsed by {usedModel}. The original text is kept on the order for audit.
                            </p>
                        )}

                        <DialogFooter className="flex-col sm:flex-row gap-2">
                            <Button variant="outline" onClick={() => setStep('input')}>
                                <ArrowLeft size={16} className="mr-2" />Back to the list
                            </Button>
                            <Button
                                disabled={acceptedLines.length === 0}
                                onClick={() => {
                                    if (confirmedRef.current) return;
                                    confirmedRef.current = true;
                                    onConfirm(acceptedLines, rawInputText, files, usageId);
                                    onOpenChange(false);
                                }}
                            >
                                <Check size={16} className="mr-2" />
                                Add {acceptedLines.length} line
                                {acceptedLines.length === 1 ? '' : 's'}
                                {unresolved.length > 0 && `, discard ${unresolved.length}`}
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default GenerateOrderDialog;
